import { CONTENT } from "@bitComponents/unifiedComponents/Stopper";
import FrontSonda from "@gcloud-npm/utils.monitoring.front-sonda";
import {
  getProductOfferingTextByType,
  getProductPromotionTextByType,
} from "./general";
import { 
  CHARACTERISTICS, 
  CQ_ACTION_CODES, 
  NETFLIX_PO,
  TEXT_TYPES_CODES,
 } from "../catalogConstants";

/**
 * Busca la información de promoción dentro de un item para el carrito
 * @param {object} cartItem Objeto que contiene la información para un item de carrito
 * @returns Información de la promo para el item que se le pasa o null si no existiese
 */
export const getPromotionInfo = (cqItem) => {
  let promotionInfo = null;
  let { productPromotions } = cqItem.productOffering;
  const { id: productOfferingId, maintainedPromotions } = cqItem.productOffering;
  // v8->v9 contingency: if they are not productPromotions,
  // we see if they are maintainedPromotions
  let isMaintainingPromo = false;
  let productWithMaintainedPromo = '';
  let productPromotion;
  if (
    !productPromotions &&
    maintainedPromotions &&
    Array.isArray(maintainedPromotions) &&
    maintainedPromotions.length > 0
  ) {
    isMaintainingPromo = true;
    // TODO ¿podrían venir varias no?
    [productPromotion] = maintainedPromotions;
    productWithMaintainedPromo = getProductOfferingTextByType(cqItem.productOffering, TEXT_TYPES_CODES.ORDINARY);
  }

  if (Array.isArray(productPromotions) && productPromotions.length > 0 ) {
    [productPromotion] = productPromotions;
    if(productPromotion && productPromotion.id){
      const promotionTextRegular = getProductPromotionTextByType(
        productPromotion,
        TEXT_TYPES_CODES.REGULAR_WITH_VALUE
      );
      const promotionTextExplanatory = getProductPromotionTextByType(
        productPromotion,
        TEXT_TYPES_CODES.LEGALS_EXPLANATORY
      );
      promotionInfo = {
        id: productPromotion.id,
        productOfferingId,
        regularText: promotionTextRegular, // TODO revisar, no se usa??
        explanatoryText: promotionTextExplanatory,
        isMaintainingPromo,
        productWithMaintainedPromo,
      };
    }
  }

  return promotionInfo;
};

/**
 * Busca la informacion de las condiciones de los productos que se van a pintar junto a los legales
 * @param {object} items Información que nos viene del api getCQPrices
 * @returns Información de las condiciones normalizada para los legales
 */
export const getProductConditions = (items, avoidAfterSaleLegalsExplanatory) => {
  const conditions = [];
  if (items.length > 0) {
    items.forEach((item) => {
      item.customerQuoteItems.forEach((cqItem) => {
        const { productOffering, actionId } = cqItem;

        if ((actionId === CQ_ACTION_CODES.CHANGE_EQUIPMENT || actionId === CQ_ACTION_CODES.EQUIPMENT_CHANGE_DUE_TO_DEPENDENT_OFFER) && avoidAfterSaleLegalsExplanatory) {
          return;
        }

        if (productOffering) {
          const conditionsText = getProductOfferingTextByType(
            productOffering,
            TEXT_TYPES_CODES.LEGALS_EXPLANATORY
          );
          const productOfferingName = getProductOfferingTextByType(
            productOffering,
            TEXT_TYPES_CODES.ORDINARY
          );
          const condition = {
            productOfferingId: productOffering.id,
            conditionText: conditionsText,
            productOfferingName,
          };
          conditions.push(condition);
        }
      });
    });
  }
  return conditions;
};

export const prepareDisagregatedDataIn = (data) => {
  let disagregatedDataIn = {};
  const productOfferings = [];
  if (data) {
    // inclusion po pikolin
    const { allPOsToBuy, poN, netflixPlan, poR, storageId, colorId } = {
      ...data,
    };
    allPOsToBuy.forEach((poToBuy) => {
      let productOffering = {
        id: poToBuy,
      };
      if (poToBuy === poN) {
        productOffering.characteristics = [
          {
            id: CHARACTERISTICS.NETFLIX,
            value: netflixPlan,
          },
        ];
      }
      if (poToBuy === poR) {
        productOffering.characteristics = [
          {
            id: CHARACTERISTICS.COLOR,
            value: colorId,
          },
          {
            id: CHARACTERISTICS.STORAGE,
            value: storageId,
          },
        ];
      }
      productOfferings.push(productOffering);
    });
  }
  disagregatedDataIn = {
    productOfferings,
  };
  return disagregatedDataIn;
};


export const detectNetflixPOatDisagregate = (disagregateResponse) => {
  const { productOfferings } = disagregateResponse;
  if (productOfferings && Array.isArray(productOfferings)) {
    const netflixPOInfo = productOfferings.find(
      (poInfo) => poInfo.id === NETFLIX_PO
    );

    if (netflixPOInfo) {
      return true;
    }
  }

  return false;
};

/**
 * Function to check if there is more than one cent of difference between two prices
 * @param sumPrice first price
 * @param price second price
 * @returns {boolean} True if the difference is higher than one cent.
 */
const evaluateCentGap = (sumPrice, price) => {
  const floatSumPrice = parseFloat(sumPrice);
  const floatPrice = parseFloat(price);
  const absDiffPrice = Math.abs(floatSumPrice - floatPrice);
  const isEnoughDiff = absDiffPrice <= 0.01;
  return !isEnoughDiff;
};

/**
 * Function to check if Telco prices match with Excel prices and send frontSonda if they did not match
 * @param {object} cartInfo Information to check
 * @param {string} poId fusion/miMovistar PO which has the client
 * @param {string} cqId purchasing CQ Id
 */
export const evaluateCartConsistency = (cartInfo, poId, cqId) => {
  if (!poId) {
    return null;
  }
  const frontSonda = new FrontSonda({
    processName: "evaluateCartConsistency",
    filePath: "sagas/normalizers/cq",
  });
  try {
    if (cartInfo && cartInfo && cartInfo.recurringCharges) {
      const {
        groupPrice,
        products,
        globalDiscount,
      } = { ...cartInfo.recurringCharges };
      let { groupPriceBeforeDiscounts } = { ...cartInfo.recurringCharges };
      groupPriceBeforeDiscounts = groupPriceBeforeDiscounts || groupPrice;
      let sumProductsPricesBeforeDiscounts = 0;
      let sumProductsPrices = 0;
      if (globalDiscount && globalDiscount.discount) {
        sumProductsPrices += globalDiscount.discount;
      }
      if (products && Array.isArray(products)) {
        products.forEach((item) => {
          let { priceBeforeDiscounts, finalPrice } = item;
          finalPrice = finalPrice || 0;
          priceBeforeDiscounts = priceBeforeDiscounts || finalPrice;
          priceBeforeDiscounts = priceBeforeDiscounts || 0;
          sumProductsPricesBeforeDiscounts += priceBeforeDiscounts;
          sumProductsPrices += finalPrice;
        });
      }
      if (
        (groupPriceBeforeDiscounts &&
          evaluateCentGap(
            sumProductsPricesBeforeDiscounts,
            groupPriceBeforeDiscounts
          )) ||
        (groupPrice && evaluateCentGap(groupPrice, sumProductsPrices))
      ) {
        const { search } = window.location;
        frontSonda.severalMessage({
          info: ` Precios disgregados y acumulados inconsistentes: Parámetros: ${search}, precio Telco acumulado sin promo: ${groupPriceBeforeDiscounts}, precio acumulado calculado excel sin promo: ${sumProductsPricesBeforeDiscounts}, precio Telco acumulado promocionado: ${groupPrice}, precio acumuladio calculado excel promocionado: ${sumProductsPrices}, parque origen: ${poId}, customerQuote: ${cqId}.`,
        });
      }
    } else {
      frontSonda.errorMessage({
        info: "error al leer la información del carro",
      });
    }
  } catch (error) {
    frontSonda.errorMessage({
      info: "Error inesperado al evaluar la consistencia del carro",
    });
  }
};

/**
 * Function to get the promotion info from disgregateProduct API to print in cart
 * @param {object} disagregateProducts response of disgregateProduct API
 * @param {string} specificTax TAX to get the proper prices
 * @param {string} mainPO PO id to find the promotion info
 * @returns {object} promotion info.
 */
const getPromoFromDisagregate = (disagregateProducts, mainPO, cartInfo) => {
  const promotionInfo = [];
  const { productOfferings } = disagregateProducts;
  if (productOfferings && Array.isArray(productOfferings)) {
    const mainPOInfo = productOfferings.find((poInfo) => poInfo.id === mainPO);
    if (mainPOInfo) {
      const { promotions } = mainPOInfo;

      promotions.forEach((promotion) => {
        if (promotion.overrideModularPriceCalculation) {
          const { taxName } = cartInfo.recurringCharges.products.find(
            (poInfo) => poInfo.productOffering.id === mainPO
          );
          const textObj = promotion.texts.find(
            (textInfo) => textInfo.typeId === TEXT_TYPES_CODES.BRIEF
          );
          const price = promotion.prices.find(
            (pricesInfo) => pricesInfo.taxName === taxName
          );
          const { id } = promotion;
          const promotionInfoObj = {
            price,
            promoId: id,
          };

          if (textObj && textObj.text) {
            promotionInfoObj.text = textObj.text;
          }

          promotionInfo.push(promotionInfoObj);
        }
      });
    }
  }
  return promotionInfo;
};

export const removeNetflixFromCartInfo = (cartInfo) => {
  const cartInfoAux = { ...cartInfo };
  const { recurringCharges } = cartInfoAux;
  if (recurringCharges.products && Array.isArray(recurringCharges.products)) {
    const netflixIndex = recurringCharges.products.findIndex(
      (product) =>
        product.productOffering && product.productOffering.id === NETFLIX_PO && !product.isDowngraded
    );
    if (netflixIndex > -1) {
      recurringCharges.products.splice(netflixIndex, 1);
    }
  }

  return cartInfoAux;
};

/**
 * Método que modifica la información del carrito si el pO a contratar tiene promoción global
 * @param {object} cqPricesResponse Salida del api cqPrices
 * @param {object} disagregateResponse Salida del api disgregar
 * @param {string} productOfferingToBuy Product offering que se está contratando
 * @param {object} cartInfo Información del carrito que se va a modificar
 * @returns Nuevo carrito con las promos específicas aplicadas
 */
export const increaseCartWithDisagregateInfo = (
  disagregateResponse,
  productOfferingToBuy,
  cartInfo
) => {
  const cartInfoModified = JSON.parse(JSON.stringify(cartInfo));
  const promotionsInfo = getPromoFromDisagregate(
    disagregateResponse,
    productOfferingToBuy,
    cartInfo
  );

  if (
    cartInfo &&
    cartInfo &&
    cartInfo.recurringCharges &&
    cartInfo.recurringCharges.products
  ) {
    cartInfo.recurringCharges.products.forEach((product, index) => {
      const { productOffering } = product;
      if (productOffering && productOffering.id) {
        const module = disagregateResponse.modules.find(
          (modulesItem) => modulesItem.id === productOffering.id
        );
        if (module) {
          const productToChange =
            cartInfoModified.recurringCharges.products[index];
          productToChange.order = module.moduleOrder;
          productToChange.secondaryOrder = module.subCategoryOrder;
        }
      }
    });
    cartInfoModified.recurringCharges.products.sort((productA, productB) => {
      return (
        productA.order - productB.order ||
        productA.secondaryOrder - productB.secondaryOrder
      );
    });
  }

  if (promotionsInfo && Array.isArray(promotionsInfo)) {
    promotionsInfo.forEach((promotionInfo) => {
      const globalDiscount = {
        text: null,
        discount: null,
      };

      if (promotionInfo.text) {
        globalDiscount.text = promotionInfo.text;
      }

      if (
        promotionInfo.price &&
        promotionInfo.price.recurringCharges &&
        promotionInfo.price.recurringCharges.discount
      ) {
        if (
          cartInfo &&
          cartInfo &&
          cartInfo.recurringCharges &&
          cartInfo.recurringCharges.products
        ) {
          globalDiscount.discount =
            promotionInfo.price.recurringCharges.discount;
          cartInfoModified.recurringCharges.globalDiscount = globalDiscount;
          const { promoId } = promotionInfo;
          cartInfo.recurringCharges.products.forEach((product, index) => {
            const { promotionId } = product;
            if (promotionId && promoId && promotionId === promoId) {
              const productToChange =
                cartInfoModified.recurringCharges.products[index];
              productToChange.finalPrice = productToChange.priceBeforeDiscounts;
              productToChange.priceBeforeDiscounts = null;
            }
          });
        }
      }
      if (
        promotionInfo.price &&
        promotionInfo.price.oneTimeCharges &&
        promotionInfo.price.oneTimeCharges.discount
      ) {
        if (
          cartInfo &&
          cartInfo &&
          cartInfo.oneTimeCharges &&
          cartInfo.oneTimeCharges.products
        ) {
          globalDiscount.discount = promotionInfo.price.oneTimeCharges.discount;
          cartInfoModified.oneTimeCharges.globalDiscount = globalDiscount;
        }
      }
    });
  }
  return cartInfoModified;
};

/**
 * Método que elimina los precios del desglose del carro
 * @param {object} cartInfo Información del carrito que se va a modificar
 * @returns Nuevo carrito sin los precios en el desglose
 */
export const removeCartProductsPrice = (cartInfo) => {
  const cartInfoModified = { ...cartInfo };

  if (
    cartInfo &&
    cartInfo &&
    cartInfo.recurringCharges &&
    cartInfo.recurringCharges.products
  ) {
    cartInfo.recurringCharges.products.forEach((product, index) => {
      const productToChange = cartInfoModified.recurringCharges.products[index];
      productToChange.finalPrice = null;
      productToChange.priceBeforeDiscounts = null;
    });
  }
  if (
    cartInfo &&
    cartInfo &&
    cartInfo.oneTimeCharges &&
    cartInfo.oneTimeCharges.products
  ) {
    cartInfo.oneTimeCharges.products.forEach((product, index) => {
      const productToChange = cartInfoModified.oneTimeCharges.products[index];
      productToChange.finalPrice = null;
      productToChange.priceBeforeDiscounts = null;
    });
  }
  return cartInfoModified;
};
